import React, { memo, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { TextData } from '../../shared/dto/EntityRepository';
import BlockWithVisible from './BlockWithVisible';
import { shrinkString } from '../../shared/util/shrinkString';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';
import { useTextListMarqueeStore } from '../../shared/store/textListMarqueeStore';
import { useTextIndexStore } from '../../shared/store/TextIndexStore';

export interface TextInfoProps {
  data: TextData;
  handleTextClick: (data: TextData, e: MouseEvent<HTMLElement>) => void;
}

const TextInfo: React.FC<TextInfoProps> = ({
  data,
  handleTextClick,
}) => {
  const textId = String(data.id);
  const { isEditMode } = useEditMode();
  // Per-row selectors: only this row re-renders when its selection/active snapshot changes.
  const isRowActive = useTextViewStore((s) =>
    !!s.activeText && String(s.activeText.id) === textId,
  );
  const isInMulti = useTextViewStore((s) =>
    s.multiSelectedText.some((t) => String(t.id) === textId),
  );
  const { deviceType } = useDeviceStore();
  const isIndexingMode = useTextIndexStore((s) => s.isIndexingMode);
  const isInIndexing = useTextIndexStore((s) =>
    s.selectedIndexingTextList.some((t) => String(t.id) === textId),
  );
  const addSelectedIndexingText = useTextIndexStore((s) => s.addSelectedIndexingText);
  const isHitByMarquee = useTextListMarqueeStore((s) =>
    s.hitTexts.some((t) => String(t.id) === textId),
  );
  const [isHover, setIsHover] = useState(false);
  const shrinkedTitle = useMemo(() => shrinkString(data.title, 30), [data.title]);

  const isMobile = deviceType === DeviceType.MOBILE;
  /** 인덱싱 중에는 패널 포함 행만 강조; 그 외 모바일은 단일 active만 */
  const isHighlighted = isIndexingMode
    ? isInIndexing
    : isMobile
      ? isRowActive
      : isRowActive || isInMulti;

  const hoverFromMarquee = !isMobile && isHitByMarquee;
  const effectiveHover = isHover || hoverFromMarquee;

  const handleRowClick = (e: MouseEvent<HTMLElement>) => {
    if (isIndexingMode) {
      addSelectedIndexingText(data);
    } else {
      handleTextClick(data, e);
    }
  };

  return (
    <TextInfoComp
      {...(!isMobile ? { 'data-text-id': data.id } : {})}
      onClick={handleRowClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      $isEditMode={isEditMode}
      $isPublic={data.isPublic}
      $isMobile={isMobile}
    >
      <TitleBlock $isActive={isHighlighted} $isHover={effectiveHover}>
        {shrinkedTitle}
      </TitleBlock>
      <BlockWithVisible
        width={"2"}
        value={data.source.author}
        isHover={effectiveHover}
        isActive={isHighlighted}
      />
      <BlockWithVisible
        width={"1"}
        value={data.prodYear === null ? '-' : data.prodYear}
        isHover={effectiveHover}
        isActive={isHighlighted}
      />
      {!isMobile && <SpaceBlock />}
    </TextInfoComp>
  )
}

const TextInfoComp = styled.article<{ $isEditMode: boolean; $isPublic: boolean; $isMobile: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: ${({ $isMobile }) => $isMobile ? '38px' : '40px'};
  min-height: 38px;
  cursor: pointer;

  user-select: ${({ $isEditMode }) => $isEditMode ? 'none' : 'auto'};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  transition: padding-left 0.3s ease-in-out;
  opacity: ${({ $isPublic }) => (!$isPublic) ? 0.6 : 1};
`;

const TitleBlock = styled.div<{ $isActive: boolean; $isHover: boolean }>`
  flex: 3;
  text-decoration: ${({ $isActive }) => $isActive ? 'underline' : 'none'};

  display: flex;
  align-items: center;
  background-color: ${({ theme, $isHover, $isActive }) =>
    $isActive
      ? theme.colors.color_Gray_06
      : $isHover
        ? theme.colors.color_Gray_07
        : 'transparent'};

  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  padding-left: 4px;
  cursor: pointer;
  user-select: none;
`

const SpaceBlock = styled.div`
  flex: 0.12;
  
  display: flex;
  align-items: center;
  justify-content: center;

  border-bottom: 1px solid ${({ theme }) => theme.colors.color_White};
`

export default memo(TextInfo);
